import type { FC } from "react";
import { useState } from "react";

import { useQuery } from "@tanstack/react-query";
import { useCurrentStateAndParams } from "@uirouter/react";
import dayjs from "dayjs";
import ReactDOMServer from "react-dom/server";
import styled from "styled-components";

import {
    dayAndShortMonth,
    shortMonthAndShortYear,
    shortMonthDayAndYearWithDayOfWeek,
} from "@similarweb/pro-logic-components";
import { colorsPalettes, setFontVariant } from "@similarweb/styles";
import { Switcher } from "@similarweb/ui-components";

import { toggleItems } from "app/components/widget/widget-utilities/time-granularity";
import { translate } from "app/filters/i18nFilter";
import { abbrNumberFilter } from "app/filters/ngFilters";
import { Loader } from "app/pages/keyword-research/keyword-analysis/common-components/clicks-over-time-graph/Loader";
import { CircleSwitcherItemStyled } from "app/components/keyword-research/clicks-over-time-graph/styledComponents";
import { EGraphGranularities, graphGranularityToString } from "app/common/keyword-research/serp-players/consts";
import BoxTitle from "pro-features/components/BoxTitle/src/BoxTitle";
import {
    BaseFlex,
    CenteredFlexColumn,
    FlexColumn,
    FlexRow,
} from "pro-features/styled components/StyledFlex/src/StyledFlex";
import { DefaultFetchService } from "app/services/fetchService";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import Chart from "pro-features/components/Chart/src/Chart";
import { chartTypes } from "app/UtilitiesAndConstants/Constants/ChartTypes";
import combineConfigs from "pro-features/components/Chart/src/combineConfigs";
import noLegendConfig from "pro-features/components/Chart/src/configs/legend/noLegendConfig";
import xAxisLabelsConfig from "pro-features/components/Chart/src/configs/xAxis/xAxisLabelsConfig";
import xAxisCrosshair from "pro-features/components/Chart/src/configs/xAxis/xAxisCrosshair";
import tooltipContainerConfig from "pro-features/components/Chart/src/configs/tooltip/tooltipContainerConfig";
import { NoData } from "pro-features/components/NoData/src/NoData";
import noMarkerConfig from "pro-features/components/Chart/src/configs/marker/noMarkerConfig";
import {
    BoldSubtitle,
    BoldSubtitle as TooltipClicks,
} from "pro-features/styled components/StyledBoxSubtitle/src/StyledBoxSubtitle";

import { useLandingPagesEndpoints } from "./landingPagesEndpoints";
import { sourceTypeMap } from "app/common/keyword-research/landing-pages/landingPagesTypes";
import { allTrackers } from "app/services/track/track";

const GRAPH_GRANULARITIES = [toggleItems.Daily.title, toggleItems.Weekly.title, toggleItems.Monthly.title];

const Header = styled(FlexRow)`
    justify-content: space-between;
    align-items: center;
    padding: 24px 24px 0 24px;
`;

const BoxTitleContainer = styled(FlexRow)`
    line-height: 1.2;
    ${setFontVariant("subtitle1", colorsPalettes.carbon[500])};
`;

const LoaderContainer = styled(FlexColumn)`
    justify-content: center;
    width: 100%;
    height: 368px;
`;

const ChartContainerNoData = styled(CenteredFlexColumn)`
    width: 100%;
    height: 368px;
`;

const TooltipDate = styled.span`
    ${setFontVariant("subtitle2", colorsPalettes.carbon[300])};
`;

const TooltipWrapper = styled(BaseFlex)`
    width: 263px;
    ${BoldSubtitle} {
        width: auto;
    }
`;

const ChartWrapper = styled.div`
    margin: 24px;
`;

interface IOrganicLandingPageEnrichedTrendProps {
    selectedDomain: string;
    landingPage: string;
}

export const OrganicLandingPageEnrichedTrend: FC<IOrganicLandingPageEnrichedTrendProps> = ({
    selectedDomain,
    landingPage,
}) => {
    const { params } = useCurrentStateAndParams();
    const { to, from, isWindow } = proNavigator.getApiParams();
    const endpoints = useLandingPagesEndpoints();
    const [selectedGranularity, setSelectedGranularity] = useState<EGraphGranularities>(EGraphGranularities.WEEKLY);
    const apiParams = {
        country: params.country,
        webSource: params.webSource,
        includeSubDomains: params.isWWW === "*",
        to,
        from,
        isWindow,
        key: selectedDomain,
        TimeGranularity: graphGranularityToString[selectedGranularity],
        landingPage,
        rowsPerPage: 50,
        sourceType: sourceTypeMap[params.selectedPageTab],
    };

    const { data, isSuccess, isLoading, isError } = useQuery({
        queryKey: [endpoints.tableDrillDownTrend, ...Object.values(apiParams)],
        select(dto: { Data: Record<string, number> }) {
            const itemsData = Object.entries(dto.Data)
                .map(([date, clicks]) => {
                    return { x: dayjs.utc(date).valueOf(), y: clicks };
                })
                .sort((a, b) => (a.x > b.x ? 1 : -1));
            return [{ data: itemsData, color: colorsPalettes.blue["400"] }];
        },
        queryFn: ({ signal }) => {
            return DefaultFetchService.getInstance().get(endpoints.tableDrillDownTrend, apiParams, {
                cancellation: signal,
            });
        },
    });

    const handleGranularityChange = (granularity: EGraphGranularities) => {
        allTrackers.dispatchTrackEvent(
            "Button",
            "click",
            `trend granularity/${graphGranularityToString[granularity] || ""}`,
        );
        setSelectedGranularity(granularity);
    };

    return (
        <>
            <Header>
                <BoxTitle
                    Container={BoxTitleContainer}
                    tooltip={translate("organic.landing.pages.enriched.trend.title.tooltip")}
                >
                    {translate("organic.landing.pages.v2.enriched.trend.title")}
                </BoxTitle>
                <div data-automation="time-granularity-switcher">
                    <Switcher
                        onItemClick={handleGranularityChange}
                        selectedIndex={selectedGranularity}
                        customClass="CircleSwitcher"
                    >
                        {GRAPH_GRANULARITIES.map((granularity) => (
                            <CircleSwitcherItemStyled key={granularity}>
                                {translate(granularity)}
                            </CircleSwitcherItemStyled>
                        ))}
                    </Switcher>
                </div>
            </Header>
            <div>
                {isLoading ? (
                    <LoaderContainer>
                        <Loader />
                    </LoaderContainer>
                ) : null}
                {isSuccess ? (
                    <ChartWrapper>
                        <Chart
                            type={chartTypes.LINE}
                            config={getChartConfig(selectedGranularity)}
                            data={data}
                        />
                    </ChartWrapper>
                ) : null}
                {isError ? (
                    <ChartContainerNoData>
                        <NoData />
                    </ChartContainerNoData>
                ) : null}
            </div>
        </>
    );
};

const getChartConfig = (timeGranularity: EGraphGranularities) => {
    const format = timeGranularity === EGraphGranularities.MONTHLY ? shortMonthAndShortYear : dayAndShortMonth;
    // @ts-expect-error FIXME: Argument of type 'number' is not assignable to parameter of type 'boolean'
    const yAxisFormatter = ({ value }) => abbrNumberFilter()(value, 0);
    const xAxisFormatter = ({ value }) => dayjs(value).format(format);

    return combineConfigs({ yAxisFormatter, xAxisFormatter }, [
        noLegendConfig,
        xAxisLabelsConfig,
        xAxisCrosshair,
        tooltipContainerConfig,
        noMarkerConfig,
        {
            tooltip: {
                shared: true,
                outside: true,
                useHTML: true,
                formatter: function () {
                    return ReactDOMServer.renderToString(
                        <TooltipWrapper
                            alignItems={"center"}
                            justifyContent={"space-between"}
                        >
                            <TooltipDate>{dayjs(this.x).format(shortMonthDayAndYearWithDayOfWeek[0])}</TooltipDate>
                            <TooltipClicks>{yAxisFormatter({ value: this.y })}</TooltipClicks>
                        </TooltipWrapper>,
                    );
                },
            },
        },
        {
            chart: {
                height: 367,
                plotBackgroundColor: "transparent",
                style: {
                    fontFamily: "Roboto",
                },
            },
            plotOptions: {
                series: {
                    fillOpacity: 1,
                    animation: false,
                    lineWidth: 2,
                },
            },
            yAxis: [
                {
                    labels: {
                        style: {
                            fontSize: "11px",
                            color: colorsPalettes.carbon[400],
                            fontFamily: "Roboto",
                        },
                    },
                    title: {
                        text: null,
                    },
                    allowDecimals: true,
                },
            ],
            xAxis: {
                gridLineWidth: 0,
                gridLineDashStyle: "dash",
                lineColor: colorsPalettes.carbon[100],
                tickColor: colorsPalettes.carbon[100],
                gridLineColor: colorsPalettes.carbon[50],
                labels: {
                    style: {
                        fontSize: "11px",
                        color: colorsPalettes.carbon[300],
                        fontFamily: "Roboto",
                    },
                },
                minPadding: 0,
                maxPadding: 0,
                type: "datetime",
                tickPositioner: function () {
                    if (timeGranularity === EGraphGranularities.MONTHLY) {
                        this.chart.series;
                        // In order to reduce ticks due to lack of space for labels
                    } else if (timeGranularity === EGraphGranularities.WEEKLY) {
                        this.chart.series.filter((element, index) => index % 2 == 0);
                    } else {
                        this.chart.series.filter((element, index) => index % 6 == 0);
                    }
                },
            },
        },
    ]);
};
